import {BusinessKYCProcessor} from '@afriex/core/domain/enums';

import type {AfriexBusinessUser} from '@/types/AdminBusinessUser';
import {BusinessKYCDocumentType} from '@/types/BusinessKYCDocumentType';

// The shape actually read off Sumsub's raw (Record<string, unknown>)
// response for this screen. `AfriexBusinessUserMeta['kyc']['responses']`
// only models it down to `unknown` per-processor, since the payload is a
// third-party wire contract.
type SumsubDetails = {
  info?: {
    idDocs?: {idDocType?: string; number?: string; validUntil?: string}[];
  };
  fixedInfo?: {dob?: string};
};

// The ID document types our own ("local") KYC form can collect a number for.
// The form posts the number under a dynamic key — the chosen ID type itself
// (see packages/business-web/src/components/KYC/local/useKYC.ts) — rather
// than a generic ID_NUMBER field, so the server persists it at
// `meta.kyc.data.<one of these keys>`.
const ID_NUMBER_TYPES = [
  BusinessKYCDocumentType.NationalID,
  BusinessKYCDocumentType.Passport,
  BusinessKYCDocumentType.DriverLicense,
  BusinessKYCDocumentType.BVN,
] as const;

export type KYCIdentity = {
  idType: string;
  idNumber: string;
  idExpiration: string;
  dateOfBirth: string;
};

/**
 * Derives the representative's identity fields for display, per field:
 * a third-party provider's result (currently only Sumsub) first, then our
 * own ("local") KYC form's data, then a dash.
 *
 * A third-party provider verifies the document against its issuing
 * authority during review and can correct what the applicant typed (e.g.
 * `fixedInfo` vs the applicant-submitted `info` on Sumsub), so its result
 * outranks the local form whenever it's present. There is no local
 * equivalent of ID expiry — the local form never collects it — so that
 * field only ever comes from a third-party provider.
 */
export const getKYCIdentity = (
  user: AfriexBusinessUser | undefined,
): KYCIdentity => {
  const kycData = user?.meta?.kyc?.data;
  const sumsubResponse = user?.meta?.kyc?.responses?.[
    BusinessKYCProcessor.Sumsub
  ] as {details?: SumsubDetails} | undefined;
  const sumsubDetails = sumsubResponse?.details;
  const sumsubIdDoc = sumsubDetails?.info?.idDocs?.[0];

  const localIdType = ID_NUMBER_TYPES.find(
    (type) => kycData?.[type] && String(kycData[type]).trim() !== '',
  );

  return {
    idType: sumsubIdDoc?.idDocType || localIdType || '',
    idNumber:
      sumsubIdDoc?.number || (localIdType ? kycData?.[localIdType] : '') || '',
    idExpiration: sumsubIdDoc?.validUntil || '',
    dateOfBirth: sumsubDetails?.fixedInfo?.dob || kycData?.DATE_OF_BIRTH || '',
  };
};
